import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Lock, CheckCircle, AlertCircle, ChevronRight, ChevronLeft, Loader2, Building2, MapPin, Clock, Share2, ShoppingBag, Star, Info, KeyRound, User, Palette, Eye, EyeOff, Upload, FileImage, X, Package, Megaphone, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_ICONS = {
  package_selection: Package,
  owner_contact: User,
  business_info: Building2,
  contact_location: MapPin,
  hours: Clock,
  platform_access: KeyRound,
  social_media: Share2,
  brand_assets: Palette,
  file_uploads: Upload,
  services_products: ShoppingBag,
  reviews_links: Star,
  ad_campaigns: Megaphone,
  additional: Info,
};

const PACKAGE_COLORS = {
  website_only: { bg: 'bg-blue-600/20', border: 'border-blue-500', text: 'text-blue-400', ring: 'ring-blue-500' },
  seo_local: { bg: 'bg-green-600/20', border: 'border-green-500', text: 'text-green-400', ring: 'ring-green-500' },
  marketing_platform: { bg: 'bg-purple-600/20', border: 'border-purple-500', text: 'text-purple-400', ring: 'ring-purple-500' },
  full_suite: { bg: 'bg-amber-600/20', border: 'border-amber-500', text: 'text-amber-400', ring: 'ring-amber-500' },
};

const DEFAULT_HOURS = {
  monday: { open: '09:00', close: '17:00', closed: false },
  tuesday: { open: '09:00', close: '17:00', closed: false },
  wednesday: { open: '09:00', close: '17:00', closed: false },
  thursday: { open: '09:00', close: '17:00', closed: false },
  friday: { open: '09:00', close: '17:00', closed: false },
  saturday: { open: '10:00', close: '14:00', closed: false },
  sunday: { open: '', close: '', closed: true },
};

export default function ClientOnboardingForm() {
  const { accessCode } = useParams();
  const [codeInput, setCodeInput] = useState('');
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [completionResult, setCompletionResult] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    if (accessCode && accessCode !== ':accessCode') {
      setCodeInput(accessCode);
      verifyCode(accessCode);
    }
  }, [accessCode]);

  const verifyCode = async (code) => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API}/api/onboarding/form/${code}`);
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || 'Invalid access code');
      }
      const data = await resp.json();
      if (data.already_submitted) {
        setSubmitted(true);
        setVerified(true);
        setFormData(data);
      } else {
        setFormData(data);
        setVerified(true);
        if (data.selected_package) {
          setSelectedPackage(data.selected_package);
        }
        const initial = {};
        data.sections.forEach(s => {
          s.fields.forEach(f => {
            if (f.value !== null && f.value !== undefined) {
              initial[f.key] = f.value;
            } else if (f.type === 'hours') {
              initial[f.key] = DEFAULT_HOURS;
            } else if (f.type === 'list' || f.type === 'checkbox_group') {
              initial[f.key] = [];
            } else if (f.type === 'boolean') {
              initial[f.key] = false;
            } else if (f.type === 'file') {
              initial[f.key] = null;
            } else if (f.type === 'file_multi') {
              initial[f.key] = [];
            } else {
              initial[f.key] = '';
            }
          });
        });
        setValues(initial);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const updateValue = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const code = accessCode || codeInput;
      const resp = await fetch(`${API}/api/onboarding/form/${code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_data: values }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Submission failed');
      setSubmitted(true);
      setCompletionResult(data.completion);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  const saveProgress = async (silent = false) => {
    if (!verified) return;
    setSaving(true);
    try {
      const code = accessCode || codeInput;
      const resp = await fetch(`${API}/api/onboarding/form/${code}/save`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_data: values, current_section: currentSection }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setLastSaved(new Date());
        setCompletionResult(data.completion);
        if (!silent) {
          setSaveMessage(`Saved! ${data.completion?.percentage || 0}% complete`);
          setTimeout(() => setSaveMessage(''), 3000);
        }
      }
    } catch (err) {
      if (!silent) setSaveMessage('Failed to save');
    }
    setSaving(false);
  };

  // Auto-save when switching sections
  const handleSectionChange = (newSection) => {
    if (newSection !== currentSection && verified && !submitted) {
      saveProgress(true); // silent save
    }
    setCurrentSection(newSection);
  };

  // Access code entry screen
  if (!verified) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="onboarding-code-screen">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600/20 mb-4">
              <Lock className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Business Onboarding</h1>
            <p className="text-slate-400 text-sm">Enter your private access code to continue</p>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">Access Code</label>
            <Input
              data-testid="access-code-input"
              value={codeInput}
              onChange={e => setCodeInput(e.target.value.toUpperCase())}
              placeholder="Enter your code"
              className="bg-slate-700 border-slate-600 text-white text-center text-xl tracking-widest font-mono h-14"
              maxLength={8}
            />
            {error && <p className="text-red-400 text-sm mt-2" data-testid="code-error">{error}</p>}
            <Button
              data-testid="verify-code-btn"
              onClick={() => verifyCode(codeInput)}
              disabled={loading || codeInput.length < 4}
              className="w-full mt-4 bg-blue-600 hover:bg-blue-700 h-12"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {loading ? 'Verifying...' : 'Access Form'}
            </Button>
          </div>
          <p className="text-center text-slate-500 text-xs mt-4">
            Your access code was sent to your email by Local Branding Pro
          </p>
        </div>
      </div>
    );
  }

  // Already submitted
  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="onboarding-submitted-screen">
        <div className="max-w-lg w-full text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-600/20 mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Profile Submitted!</h1>
          <p className="text-slate-400 mb-6">
            Thank you, <span className="text-white font-semibold">{formData?.business_name}</span>. Your business profile has been received.
          </p>
          {completionResult && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 text-left">
              <div className="flex items-center justify-between mb-3">
                <span className="text-slate-300 font-medium">Profile Completion</span>
                <span className="text-2xl font-bold text-blue-400">{completionResult.percentage}%</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2.5 mb-4">
                <div className="bg-blue-500 h-2.5 rounded-full transition-all" style={{ width: `${completionResult.percentage}%` }} />
              </div>
              {completionResult.missing_fields?.length > 0 && (
                <div>
                  <p className="text-slate-400 text-sm mb-2">Still needed:</p>
                  <ul className="space-y-1">
                    {completionResult.missing_fields.map((f) => (
                      <li key={f} className="text-amber-400 text-sm flex items-center gap-2">
                        <AlertCircle className="w-3 h-3 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {completionResult && completionResult.percentage < 100 && (
            <Button
              className="mt-6 bg-blue-600 hover:bg-blue-700"
              onClick={() => setSubmitted(false)}
              data-testid="go-back-edit-btn"
            >
              Go Back & Complete Missing Fields
            </Button>
          )}
          <p className="text-slate-500 text-sm mt-6">Our team will review your submission and reach out with next steps.</p>
        </div>
      </div>
    );
  }

  if (!formData) return null;

  const allSections = formData.sections || [];
  // Filter sections based on selected package
  const sections = allSections.filter(s => {
    if (!s.requires_package) return true;
    if (!selectedPackage) return true; // Show all until package is selected
    return s.requires_package.includes(selectedPackage);
  });
  const section = sections[currentSection];
  const totalSections = sections.length;
  const SectionIcon = SECTION_ICONS[section?.id] || Building2;

  const sectionCompletion = () => {
    if (!section) return 0;
    const required = section.fields.filter(f => f.required);
    if (!required.length) return 100;
    const filled = required.filter(f => {
      const v = values[f.key];
      if (f.type === 'hours') return v && typeof v === 'object' && Object.values(v).some(d => !d.closed && d.open);
      if (f.type === 'list' || f.type === 'checkbox_group') return Array.isArray(v) && v.length > 0;
      if (f.type === 'file') return v !== null && v?.file_id;
      if (f.type === 'file_multi') return Array.isArray(v) && v.length > 0;
      return v !== '' && v !== null && v !== undefined;
    });
    return Math.round((filled.length / required.length) * 100);
  };

  const overallCompletion = () => {
    let total = 0, filled = 0;
    sections.forEach(s => {
      s.fields.filter(f => f.required).forEach(f => {
        total++;
        const v = values[f.key];
        if (f.type === 'hours') {
          if (v && typeof v === 'object' && Object.values(v).some(d => !d.closed && d.open)) filled++;
        } else if (f.type === 'list' || f.type === 'checkbox_group') {
          if (Array.isArray(v) && v.length > 0) filled++;
        } else if (f.type === 'file') {
          if (v !== null && v?.file_id) filled++;
        } else if (f.type === 'file_multi') {
          if (Array.isArray(v) && v.length > 0) filled++;
        } else {
          if (v !== '' && v !== null && v !== undefined) filled++;
        }
      });
    });
    return total > 0 ? Math.round((filled / total) * 100) : 0;
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="onboarding-form-screen">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-white">{formData.business_name}</h1>
              <p className="text-slate-400 text-xs">Business Profile Setup</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-400 text-xs">Overall</span>
              <div className="flex items-center gap-2">
                <div className="w-20 bg-slate-700 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${overallCompletion()}%` }} />
                </div>
                <span className="text-blue-400 text-sm font-bold">{overallCompletion()}%</span>
              </div>
            </div>
          </div>
          {/* Section nav */}
          <div className="flex gap-1.5 mt-3 overflow-x-auto pb-1">
            {sections.map((s, i) => {
              const Icon = SECTION_ICONS[s.id] || Building2;
              const active = i === currentSection;
              return (
                <button
                  key={s.id}
                  onClick={() => handleSectionChange(i)}
                  data-testid={`section-nav-${s.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-all ${
                    active ? 'bg-blue-600 text-white' : 'bg-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{s.title}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          {/* Section Header */}
          <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                <SectionIcon className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-white font-semibold">{section?.title}</h2>
                <p className="text-slate-400 text-xs">Step {currentSection + 1} of {totalSections}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${sectionCompletion() === 100 ? 'text-green-400' : 'text-slate-400'}`}>
                {sectionCompletion()}% done
              </span>
              {sectionCompletion() === 100 && <CheckCircle className="w-4 h-4 text-green-400" />}
            </div>
          </div>

          {/* Fields */}
          <div className="p-6 space-y-5">
            {section?.description && (
              <div className="p-3 bg-blue-900/20 border border-blue-800/30 rounded-lg text-blue-300 text-sm flex items-start gap-2">
                <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{section.description}</span>
              </div>
            )}
            {section?.fields.map(field => (
              <FieldRenderer
                key={field.key}
                field={field}
                value={values[field.key]}
                onChange={(val) => {
                  updateValue(field.key, val);
                  if (field.key === 'package_type') {
                    setSelectedPackage(val);
                    setCurrentSection(0); // Reset to first section
                  }
                }}
                slug={formData.client_slug}
                allValues={values}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
            <Button
              data-testid="prev-section-btn"
              variant="outline"
              onClick={() => handleSectionChange(currentSection - 1)}
              disabled={currentSection === 0}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>

            <div className="flex items-center gap-2">
              {/* Save Progress Button */}
              <Button
                data-testid="save-progress-btn"
                variant="outline"
                onClick={() => saveProgress(false)}
                disabled={saving}
                className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/30"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                {saving ? 'Saving...' : 'Save Progress'}
              </Button>

              {currentSection < totalSections - 1 ? (
                <Button
                  data-testid="next-section-btn"
                  onClick={() => handleSectionChange(currentSection + 1)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  data-testid="submit-onboarding-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700 px-8"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  {submitting ? 'Submitting...' : 'Submit Profile'}
                </Button>
              )}
            </div>
          </div>

          {/* Save status bar */}
          {(saveMessage || lastSaved) && (
            <div className="px-6 py-2 border-t border-slate-700/50 flex items-center justify-between text-xs">
              {saveMessage && <span className="text-emerald-400 font-medium">{saveMessage}</span>}
              {lastSaved && !saveMessage && <span className="text-slate-500">Last saved: {lastSaved.toLocaleTimeString()}</span>}
              <span className="text-slate-500">Your progress is saved automatically when you switch sections</span>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-400 text-sm" data-testid="form-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}


function FieldRenderer({ field, value, onChange, slug, allValues }) {
  const { key, label, type, required, prefilled } = field;

  // All hooks must be at the top level (not inside conditionals)
  const [draft, setDraft] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [otherText, setOtherText] = useState('');

  // Conditional visibility: show_when logic
  if (field.show_when) {
    const dep = allValues?.[field.show_when.field];
    if (field.show_when.value && dep !== field.show_when.value) return null;
    if (field.show_when.not_value && dep === field.show_when.not_value) return null;
  }

  const labelEl = (
    <div className="flex items-center gap-2 mb-1.5">
      <label className="text-sm font-medium text-slate-300">{label}</label>
      {required && <span className="text-xs text-red-400 font-medium">Required</span>}
      {prefilled && <span className="text-xs text-green-400/70 bg-green-400/10 px-1.5 py-0.5 rounded">Pre-filled</span>}
    </div>
  );

  if (type === 'package_select') {
    const options = field.options || [];
    return (
      <div data-testid={`field-${key}`}>
        {labelEl}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          {options.map(opt => {
            const colors = PACKAGE_COLORS[opt.value] || PACKAGE_COLORS.website_only;
            const selected = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                data-testid={`package-${opt.value}`}
                className={`text-left p-4 rounded-xl border-2 transition-all ${
                  selected
                    ? `${colors.border} ${colors.bg} ring-2 ${colors.ring}`
                    : 'border-slate-600 bg-slate-700/50 hover:border-slate-500'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`font-semibold text-sm ${selected ? colors.text : 'text-slate-200'}`}>
                    {opt.label}
                  </span>
                  {selected && <CheckCircle className={`w-4 h-4 ${colors.text}`} />}
                </div>
                <p className="text-xs text-slate-400">{opt.description}</p>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === 'select') {
    const options = field.options || [];
    return (
      <div data-testid={`field-${key}`}>
        {labelEl}
        <select
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className="w-full h-10 bg-slate-700 border border-slate-600 text-white rounded-md px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-testid={`select-${key}`}
        >
          <option value="" className="text-slate-400">Select...</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  if (type === 'select_other') {
    const options = field.options || [];
    const isOther = value && !options.includes(value);
    return (
      <div data-testid={`field-${key}`}>
        {labelEl}
        <select
          value={isOther ? '__other__' : (value || '')}
          onChange={e => {
            if (e.target.value === '__other__') {
              onChange(otherText || '');
            } else {
              onChange(e.target.value);
              setOtherText('');
            }
          }}
          className="w-full h-10 bg-slate-700 border border-slate-600 text-white rounded-md px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-testid={`select-${key}`}
        >
          <option value="" className="text-slate-400">Select...</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
          <option value="__other__">Other (type below)</option>
        </select>
        {isOther && (
          <Input
            value={value || ''}
            onChange={e => { onChange(e.target.value); setOtherText(e.target.value); }}
            placeholder="Please specify..."
            className="bg-slate-700 border-slate-600 text-white mt-2"
            data-testid={`other-input-${key}`}
          />
        )}
      </div>
    );
  }

  if (type === 'checkbox_group') {
    const options = field.options || [];
    const selected = Array.isArray(value) ? value : [];
    return (
      <div data-testid={`field-${key}`}>
        {labelEl}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
          {options.map(opt => {
            const checked = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  if (checked) {
                    onChange(selected.filter(s => s !== opt));
                  } else {
                    onChange([...selected, opt]);
                  }
                }}
                className={`flex items-center gap-2 text-left text-xs px-3 py-2.5 rounded-lg border transition-all ${
                  checked
                    ? 'border-blue-500 bg-blue-600/20 text-blue-300'
                    : 'border-slate-600 bg-slate-700/50 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                }`}
                data-testid={`checkbox-${key}-${opt.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border ${
                  checked ? 'bg-blue-600 border-blue-500' : 'border-slate-500'
                }`}>
                  {checked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </div>
                <span>{opt}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === 'textarea') {
    return (
      <div data-testid={`field-${key}`}>
        {labelEl}
        <Textarea
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          rows={4}
          className="bg-slate-700 border-slate-600 text-white"
          data-testid={`input-${key}`}
        />
      </div>
    );
  }

  if (type === 'boolean') {
    return (
      <div className="flex items-center justify-between" data-testid={`field-${key}`}>
        {labelEl}
        <button
          data-testid={`toggle-${key}`}
          onClick={() => onChange(!value)}
          className={`w-12 h-6 rounded-full transition-colors ${value ? 'bg-blue-600' : 'bg-slate-600'} relative`}
        >
          <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${value ? 'translate-x-6' : 'translate-x-0.5'}`} />
        </button>
      </div>
    );
  }

  if (type === 'hours') {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const hours = value || DEFAULT_HOURS;
    return (
      <div data-testid={`field-${key}`}>
        {labelEl}
        <div className="space-y-2">
          {days.map(day => (
            <div key={day} className="flex items-center gap-3 bg-slate-700/50 rounded-lg p-2.5">
              <span className="w-24 text-sm text-slate-300 capitalize font-medium">{day}</span>
              <button
                onClick={() => {
                  const updated = { ...hours, [day]: { ...hours[day], closed: !hours[day]?.closed } };
                  onChange(updated);
                }}
                className={`px-2 py-1 rounded text-xs font-medium ${hours[day]?.closed ? 'bg-red-900/40 text-red-400' : 'bg-green-900/40 text-green-400'}`}
              >
                {hours[day]?.closed ? 'Closed' : 'Open'}
              </button>
              {!hours[day]?.closed && (
                <>
                  <Input
                    type="time"
                    value={hours[day]?.open || '09:00'}
                    onChange={e => onChange({ ...hours, [day]: { ...hours[day], open: e.target.value } })}
                    className="bg-slate-700 border-slate-600 text-white h-8 w-32 text-xs"
                  />
                  <span className="text-slate-500 text-xs">to</span>
                  <Input
                    type="time"
                    value={hours[day]?.close || '17:00'}
                    onChange={e => onChange({ ...hours, [day]: { ...hours[day], close: e.target.value } })}
                    className="bg-slate-700 border-slate-600 text-white h-8 w-32 text-xs"
                  />
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'list') {
    const items = Array.isArray(value) ? value : [];
    return (
      <div data-testid={`field-${key}`}>
        {labelEl}
        <div className="flex gap-2 mb-2">
          <Input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && draft.trim()) {
                onChange([...items, draft.trim()]);
                setDraft('');
                e.preventDefault();
              }
            }}
            placeholder="Type and press Enter to add"
            className="bg-slate-700 border-slate-600 text-white"
            data-testid={`list-input-${key}`}
          />
          <Button
            type="button"
            onClick={() => {
              if (draft.trim()) {
                onChange([...items, draft.trim()]);
                setDraft('');
              }
            }}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 px-4"
          >
            Add
          </Button>
        </div>
        {items.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {items.map((item, i) => (
              <span key={`${item}-${i}`} className="inline-flex items-center gap-1 bg-slate-700 text-slate-300 text-xs px-2.5 py-1 rounded-full">
                {item}
                <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400 ml-0.5">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // File upload (single)
  if (type === 'file') {
    const handleUpload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', key.replace('upload_', ''));
      fd.append('label', label);
      try {
        const res = await fetch(`${API}/api/client/files/upload/${slug}`, { method: 'POST', body: fd });
        const data = await res.json();
        if (data.success) onChange(data.file);
      } catch (err) { console.error('Upload failed', err); }
      setUploading(false);
    };
    return (
      <div data-testid={`field-${key}`}>
        {labelEl}
        {value?.file_id ? (
          <div className="flex items-center gap-3 bg-slate-700/50 rounded-lg p-3">
            <FileImage className="w-8 h-8 text-blue-400" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{value.original_filename}</p>
              <p className="text-xs text-slate-400">{(value.size / 1024).toFixed(1)} KB</p>
            </div>
            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
            <button onClick={() => onChange(null)} className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <label className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-slate-700/30 transition-colors">
            {uploading ? <Loader2 className="w-6 h-6 text-blue-400 animate-spin" /> : <Upload className="w-6 h-6 text-slate-400" />}
            <span className="text-sm text-slate-400">{uploading ? 'Uploading...' : 'Click to upload'}</span>
            <input type="file" className="hidden" accept={field.accept || '*'} onChange={handleUpload} disabled={uploading} />
          </label>
        )}
      </div>
    );
  }

  // File upload (multiple)
  if (type === 'file_multi') {
    const files = Array.isArray(value) ? value : [];
    const handleUpload = async (e) => {
      const fileList = e.target.files;
      if (!fileList?.length) return;
      setUploading(true);
      const uploaded = [...files];
      for (const file of fileList) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', key.replace('upload_', ''));
        fd.append('label', label);
        try {
          const res = await fetch(`${API}/api/client/files/upload/${slug}`, { method: 'POST', body: fd });
          const data = await res.json();
          if (data.success) uploaded.push(data.file);
        } catch (err) { console.error('Upload failed', err); }
      }
      onChange(uploaded);
      setUploading(false);
    };
    return (
      <div data-testid={`field-${key}`}>
        {labelEl}
        {files.length > 0 && (
          <div className="space-y-2 mb-3">
            {files.map((f, i) => (
              <div key={f.original_filename || f.url || `file-${i}`} className="flex items-center gap-3 bg-slate-700/50 rounded-lg p-2.5">
                <FileImage className="w-5 h-5 text-blue-400" />
                <span className="text-sm text-white truncate flex-1">{f.original_filename}</span>
                <span className="text-xs text-slate-400">{(f.size / 1024).toFixed(1)} KB</span>
                <button onClick={() => onChange(files.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <label className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-slate-700/30 transition-colors">
          {uploading ? <Loader2 className="w-5 h-5 text-blue-400 animate-spin" /> : <Upload className="w-5 h-5 text-slate-400" />}
          <span className="text-sm text-slate-400">{uploading ? 'Uploading...' : 'Click to add files'}</span>
          <input type="file" className="hidden" multiple accept={field.accept || '*'} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    );
  }

  // Default: text, email, url, number, password
  if (type === 'password') {
    return (
      <div data-testid={`field-${key}`}>
        {labelEl}
        <div className="relative">
          <Input
            type={showPw ? 'text' : 'password'}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            className="bg-slate-700 border-slate-600 text-white pr-10"
            data-testid={`input-${key}`}
          />
          <button
            type="button"
            onClick={() => setShowPw(!showPw)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
          >
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`field-${key}`}>
      {labelEl}
      <Input
        type={type === 'number' ? 'number' : 'text'}
        value={value || ''}
        onChange={e => onChange(type === 'number' ? (e.target.value ? parseInt(e.target.value) : '') : e.target.value)}
        placeholder={type === 'url' ? 'https://...' : ''}
        className="bg-slate-700 border-slate-600 text-white"
        data-testid={`input-${key}`}
      />
    </div>
  );
}
